import { useState, useCallback, useRef, useEffect } from 'react';

// ============================================
// Types
// ============================================

export interface CameraDevice {
  deviceId: string;
  label: string;
}

export interface UseCameraOptions {
  /** Preferred facing mode for initial camera selection */
  facingMode?: 'user' | 'environment';
  /** Target resolution width */
  width?: number;
  /** Target resolution height */
  height?: number;
}

export interface UseCameraReturn {
  /** Reference to attach to a <video> element */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Whether the camera stream is active */
  isStreaming: boolean;
  /** Whether the camera is initializing */
  isInitializing: boolean;
  /** Error message if camera access failed */
  error: string | null;
  /** List of available camera devices */
  devices: CameraDevice[];
  /** Currently active device ID */
  activeDeviceId: string | null;
  /** Whether the device has multiple cameras */
  hasMultipleCameras: boolean;
  /** Start the camera stream */
  start: (deviceId?: string) => Promise<boolean>;
  /** Stop the camera stream and release resources */
  stop: () => void;
  /** Switch to a different camera by device ID */
  switchCamera: (deviceId: string) => Promise<boolean>;
  /** Cycle to the next available camera */
  cycleCamera: () => Promise<boolean>;
  /** Capture a still frame from the video stream and return as a Blob */
  capturePhoto: (options?: CapturePhotoOptions) => Promise<Blob | null>;
}

export interface CapturePhotoOptions {
  /** Maximum dimension (width or height) for the captured image. Default: 1920 */
  maxDimension?: number;
  /** MIME type for the output image. Default: 'image/jpeg' */
  mimeType?: string;
  /** Quality for lossy formats (0-1). Default: 0.85 */
  quality?: number;
}

// ============================================
// Constants
// ============================================

const DEFAULT_MAX_DIMENSION = 1920;
const DEFAULT_MIME_TYPE = 'image/jpeg';
const DEFAULT_QUALITY = 0.85;

// ============================================
// Hook
// ============================================

export const useCamera = (options: UseCameraOptions = {}): UseCameraReturn => {
  const {
    facingMode = 'environment',
    width = 1920,
    height = 1080,
  } = options;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);

  // ============================================
  // Device Enumeration
  // ============================================

  const enumerateDevices = useCallback(async (): Promise<CameraDevice[]> => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices
        .filter((d) => d.kind === 'videoinput')
        .map((d, index) => ({
          deviceId: d.deviceId,
          // Browsers may hide labels until permission is granted; provide fallback
          label: d.label || `Camera ${index + 1}`,
        }));
      setDevices(videoDevices);
      return videoDevices;
    } catch {
      return [];
    }
  }, []);

  // ============================================
  // Stop Stream
  // ============================================

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
    setActiveDeviceId(null);
  }, []);

  // ============================================
  // Start Stream
  // ============================================

  const start = useCallback(
    async (deviceId?: string): Promise<boolean> => {
      // Stop any existing stream first
      stop();

      setIsInitializing(true);
      setError(null);

      try {
        // Check for camera API support
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('cameraNotSupported');
          setIsInitializing(false);
          return false;
        }

        // Build constraints
        const videoConstraints: MediaTrackConstraints = {
          width: { ideal: width },
          height: { ideal: height },
        };

        if (deviceId) {
          videoConstraints.deviceId = { exact: deviceId };
        } else {
          videoConstraints.facingMode = { ideal: facingMode };
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });

        streamRef.current = stream;

        // Attach stream to video element
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        // Determine which device was actually selected
        const videoTrack = stream.getVideoTracks()[0];
        const settings = videoTrack?.getSettings();
        const resolvedDeviceId = settings?.deviceId || deviceId || null;
        setActiveDeviceId(resolvedDeviceId);

        // Now that we have permission, enumerate devices (labels will be visible)
        await enumerateDevices();

        setIsStreaming(true);
        setIsInitializing(false);
        return true;
      } catch (err: any) {
        const errorName = err?.name || '';

        if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
          setError('cameraPermissionDenied');
        } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
          setError('cameraNotFound');
        } else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
          setError('cameraInUse');
        } else if (errorName === 'OverconstrainedError') {
          setError('cameraOverconstrained');
        } else {
          setError('cameraUnknownError');
        }

        setIsInitializing(false);
        return false;
      }
    },
    [facingMode, width, height, stop, enumerateDevices]
  );

  // ============================================
  // Switch / Cycle Camera
  // ============================================

  const switchCamera = useCallback(
    async (deviceId: string): Promise<boolean> => {
      return start(deviceId);
    },
    [start]
  );

  const cycleCamera = useCallback(async (): Promise<boolean> => {
    if (devices.length < 2) return false;

    const currentIndex = devices.findIndex((d) => d.deviceId === activeDeviceId);
    const nextIndex = (currentIndex + 1) % devices.length;
    const nextDevice = devices[nextIndex];

    return switchCamera(nextDevice.deviceId);
  }, [devices, activeDeviceId, switchCamera]);

  // ============================================
  // Capture Photo
  // ============================================

  const capturePhoto = useCallback(
    async (captureOptions?: CapturePhotoOptions): Promise<Blob | null> => {
      const video = videoRef.current;

      if (!video || !isStreaming) {
        return null;
      }

      const maxDimension = captureOptions?.maxDimension ?? DEFAULT_MAX_DIMENSION;
      const mimeType = captureOptions?.mimeType ?? DEFAULT_MIME_TYPE;
      const quality = captureOptions?.quality ?? DEFAULT_QUALITY;

      // Get the actual video dimensions (may differ from requested)
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      if (!videoWidth || !videoHeight) {
        return null;
      }

      // Calculate scaled dimensions while maintaining aspect ratio
      let targetWidth = videoWidth;
      let targetHeight = videoHeight;

      if (videoWidth > maxDimension || videoHeight > maxDimension) {
        if (videoWidth >= videoHeight) {
          targetWidth = maxDimension;
          targetHeight = Math.round((videoHeight / videoWidth) * maxDimension);
        } else {
          targetHeight = maxDimension;
          targetWidth = Math.round((videoWidth / videoHeight) * maxDimension);
        }
      }

      // Reuse canvas to avoid repeated allocations
      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      const canvas = canvasRef.current;
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return null;
      }

      ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

      return new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (blob) => resolve(blob),
          mimeType,
          quality
        );
      });
    },
    [isStreaming]
  );

  // ============================================
  // Cleanup on Unmount
  // ============================================

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // ============================================
  // Return
  // ============================================

  return {
    videoRef,
    isStreaming,
    isInitializing,
    error,
    devices,
    activeDeviceId,
    hasMultipleCameras: devices.length > 1,
    start,
    stop,
    switchCamera,
    cycleCamera,
    capturePhoto,
  };
};